import { useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import { fetchLiveCryptoAssets } from "../../utils/cryptoApi";
import { getAssets, initiateBuy } from "../../api/apiServices";


export default function BuyCrypto() {
  const navigate = useNavigate();

  const [step, setStep] = useState("select");
  const [liveAssets, setLiveAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [nairaAmount, setNairaAmount] = useState("");
  const [loading, setLoading] = useState(true);

  const quickAmounts = [5000, 10000, 25000, 50000, 100000];

  // Fetch Live Rates
  useEffect(() => {
  const fetchAssets = async () => {
    try {
      const data = await getAssets();
      
      // Clean the data before saving to state
      const sanitizedData = data.map(asset => ({
        ...asset,
        // Remove ₦ and commas, then convert to Float
        price: parseFloat(asset.price.replace(/[₦,]/g, '')),
        // Optional: Clean up change and USD values too
        change: parseFloat(asset.change.replace(/[%,]/g, '')),
        usdPrice: parseFloat(asset.usd.replace(/[$,]/g, ''))
      }));

      setLiveAssets(sanitizedData);
    } catch (err) {
      console.error("Fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  fetchAssets();
}, []);

  const cryptoAmount = nairaAmount && selectedAsset
    ? (parseFloat(nairaAmount) / selectedAsset.price).toFixed(6)
    : "0.000000";

    const fee = nairaAmount ? (parseFloat(nairaAmount) * 0.015).toFixed(2) : "0.00";
    const total = nairaAmount ? (parseFloat(nairaAmount) * 1.015).toFixed(2) : "0.00";

    const handleConfirm = async () => {
      setStep("processing");
      try {
        
        const data = await initiateBuy(selectedAsset.symbol, parseFloat(nairaAmount));
        
        if (data.authorization_url) {
          window.location.href = data.authorization_url;
        }
    } catch (err) {
      console.error(err);
      setStep("confirm"); // go back on error
    }
  };

  const goBack = () => {
    if (step === "select" || step === "amount") navigate("/home");
    else if (step === "confirm") setStep("amount");
    else setStep("select");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Loading live rates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-28">
      <div className="bg-white px-5 pt-12 pb-4 flex items-center gap-3 border-b border-slate-100">
        <button onClick={goBack} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200">
          <i className="ri-arrow-left-line text-slate-600 text-lg"></i>
        </button>
        <h1 className="text-slate-800 font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Buy Crypto</h1>
      </div>

      <div className="px-5 py-4">
        <div className="flex gap-1.5 mb-6">
          {["Select Asset", "Enter Amount", "Confirm"].map((s, i) => {
            const idx = step === "select" ? 0 : step === "amount" ? 1 : 2;
            return (
              <div
                key={s}
                className="flex-1 h-1.5 rounded-full transition-all"
                style={{ background: i <= idx ? "#059669" : "#E2E8F0" }}
              />
            );
          })}
        </div>

        {step === "select" && (
          <div>
            <p className="text-slate-500 text-sm mb-4">Choose the crypto you want to buy with Naira</p>
            <div className="space-y-2">
              {liveAssets.map(asset => (
                <button
                  key={asset.symbol}
                  onClick={() => {
                    setSelectedAsset(asset);
                    setStep("amount");
                  }}
                  className="w-full bg-white border border-slate-200 rounded-2xl px-4 py-4 flex items-center justify-between cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/30 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden"
                    >
                      <img src={asset.icon} alt={asset.symbol} className="w-7 h-7 object-contain" />
                    </div>
                    <div className="text-left">
                      <p className="text-slate-800 text-sm font-bold">{asset.name}</p>
                      <p className="text-slate-400 text-xs">{asset.network || "Multiple Networks"}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-800 text-sm font-semibold">₦{asset.price.toLocaleString()}</p>
                    <p className={`text-xs font-semibold ${asset.changeType === "positive" ? "text-emerald-600" : "text-red-500"}`}>
                      {asset.change}%
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "amount" && selectedAsset && (
          <div>
            <div className="flex items-center gap-2 mb-5 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 w-fit">
              <div className="w-5 h-5 rounded-full flex items-center justify-center">
                <img src={selectedAsset.icon} alt={selectedAsset.symbol} className="w-7 h-7 object-contain" />
              </div>
              <span className="text-slate-700 text-sm font-semibold">{selectedAsset.name}</span>
              <button onClick={() => setStep("select")} className="text-emerald-600 text-xs cursor-pointer ml-1 font-medium">Change</button>
            </div>

            {/* You Pay */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-3">
              <p className="text-slate-400 text-xs mb-2 font-medium">You Pay (NGN)</p>
              <div className="flex items-center gap-2">
                <span className="text-emerald-600 text-2xl font-bold">₦</span>
                <input
                  type="number"
                  value={nairaAmount}
                  onChange={e => setNairaAmount(e.target.value)}
                  placeholder="0.00"
                  className="flex-1 bg-transparent text-slate-800 text-3xl font-bold placeholder-slate-200 focus:outline-none"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                />
              </div>
            </div>

            {/* Quick Amounts */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {quickAmounts.map(amt => (
                <button
                  key={amt}
                  onClick={() => setNairaAmount(String(amt))}
                  className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 text-xs whitespace-nowrap cursor-pointer hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                >
                  ₦{amt.toLocaleString()}
                </button>
              ))}
            </div>

            {/* You Receive */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4">
              <p className="text-slate-400 text-xs mb-2 font-medium">You Receive ({selectedAsset.symbol})</p>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: `${selectedAsset.color}18` }}>
                  <span className="text-xs font-bold" style={{ color: selectedAsset.color }}>{selectedAsset.symbol.charAt(0)}</span>
                </div>
                <span className="text-slate-800 text-2xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {cryptoAmount}
                </span>
                <span className="text-slate-400 text-base">{selectedAsset.symbol}</span>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 space-y-2 mb-6">
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Rate</span>
                <span className="text-slate-600">1 {selectedAsset.symbol} = {selectedAsset.price.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-400">Processing fee (1.5%)</span>
                <span className="text-slate-600">₦{parseFloat(fee).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs border-t border-slate-200 pt-2">
                <span className="text-slate-800 font-semibold">Total Debit</span>
                <span className="text-emerald-600 font-bold">₦{parseFloat(total).toLocaleString()}</span>
              </div>
            </div>

            <button
              onClick={() => nairaAmount && parseFloat(nairaAmount) >= 500 ? setStep("confirm") : undefined}
              disabled={!nairaAmount || parseFloat(nairaAmount) < 500}
              className="w-full py-4 rounded-2xl text-white font-semibold text-base whitespace-nowrap transition-all active:scale-95 disabled:opacity-40 bg-emerald-600 hover:bg-emerald-700"
            >
              Continue to Payment
            </button>
            <p className="text-center text-slate-400 text-xs mt-2">Minimum: ₦500</p>
          </div>
        )}

        {step === "confirm" && (
          <div>
            <h2 className="text-slate-800 text-xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Confirm Order</h2>
            <p className="text-slate-400 text-sm mb-6">Review your order before paying</p>
            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 mb-5">
              <div className="flex justify-between"><span className="text-slate-400 text-sm">Asset</span><span className="text-slate-800 text-sm font-bold">{selectedAsset.name} ({selectedAsset.symbol})</span></div>
              <div className="flex justify-between"><span className="text-slate-400 text-sm">You Pay</span><span className="text-slate-800 text-sm font-bold">₦{parseFloat(nairaAmount).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-400 text-sm">Fee (1.5%)</span><span className="text-slate-600 text-sm">₦{parseFloat(fee).toLocaleString()}</span></div>
              <div className="flex justify-between border-t border-slate-100 pt-3"><span className="text-slate-800 text-sm font-bold">Total Charged</span><span className="text-emerald-600 text-sm font-bold">₦{parseFloat(total).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-400 text-sm">You Receive</span><span className="text-emerald-600 text-sm font-bold">{cryptoAmount} {selectedAsset.symbol}</span></div>
              <div className="flex justify-between"><span className="text-slate-400 text-sm">Payment Method</span><span className="text-slate-700 text-sm flex items-center gap-1"><i className="ri-bank-card-line text-emerald-600"></i> Card/Transfer</span></div>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
              <i className="ri-shield-check-line text-emerald-600 text-lg mt-0.5"></i>
              <p className="text-slate-500 text-xs leading-relaxed">Your payment is processed securely. Once confirmed, crypto will be credited to your wallet automatically within 2 minutes.</p>
            </div>
            <button onClick={handleConfirm} className="w-full py-4 rounded-2xl text-white font-semibold text-base whitespace-nowrap transition-all active:scale-95 bg-emerald-600 hover:bg-emerald-700">
              <i className="ri-secure-payment-line mr-2"></i> Pay ₦{parseFloat(total).toLocaleString()} via Card/Transfer
            </button>
          </div>
        )}

        {step === "processing" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
              <i className="ri-loader-4-line text-emerald-600 text-4xl animate-spin"></i>
            </div>
            <h2 className="text-slate-800 text-xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Processing Payment</h2>
            <p className="text-slate-400 text-sm">Verifying your Paystack payment and crediting your {selectedAsset.symbol} wallet...</p>
          </div>
        )}

        {step === "success" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 border-2 border-emerald-200 flex items-center justify-center mb-6">
              <i className="ri-check-line text-emerald-600 text-4xl"></i>
            </div>
            <h2 className="text-slate-800 text-2xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Order Successful!</h2>
            <p className="text-slate-400 text-sm mb-4">Payment confirmed. Your crypto has been credited.</p>
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-6 py-4 mb-8 w-full max-w-xs">
              <p className="text-slate-400 text-xs mb-1">You received</p>
              <p className="text-emerald-600 text-3xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{cryptoAmount} {selectedAsset.symbol}</p>
              <p className="text-slate-400 text-xs mt-1">for ₦{parseFloat(nairaAmount).toLocaleString()}</p>
            </div>
            <button onClick={() => navigate("/home")} className="w-full max-w-xs py-4 rounded-2xl text-white font-semibold text-base whitespace-nowrap bg-emerald-600 hover:bg-emerald-700">Back to Home</button>
            <button onClick={() => navigate("/transactions")} className="mt-3 text-emerald-600 text-sm cursor-pointer font-medium">View Transaction</button>
          </div>
        )}
      </div>
      <MobileBottomNav />
    </div>
  );
}
